import { useGridApiRef } from '@mui/x-data-grid-pro';
import { CurrencyProvider } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { SvbrgChatMessageStatus } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatStepActions } from '~/components/ChatStep/ChatActions/ChatStepActions';
import { useChatStepState } from '~/components/ChatStep/ChatActions/ChatStepActions.hooks';
import { ChatStepAction, ChatStepState } from '~/components/ChatStep/ChatActions/ChatStepActions.types';
import { ChatSkeleton } from '~/components/ChatStep/ChatSkeleton/ChatSkeleton';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { useChatSession } from '~/modules/Module.hooks';

import { IntakeRequestStepLineItems } from './components/IntakeRequestStepLineItems/IntakeRequestStepLineItems';
import { useIntakeRequestLineItemStep, useLineItemsData } from './IntakeRequestLineItemStep.hooks';
import { gridDataToLineItem } from './IntakeRequestLineItemStep.util';

export const IntakeRequestLineItemStep = ({ role, status, createdBy, chatSessionCompleted }: ChatMessageProps) => {
  const { t: tGlobal } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequestLineItemStep' });
  const { id: sessionId } = useChatSession();
  const { data, loading, updating, save, reset } = useIntakeRequestLineItemStep({
    sessionId,
    status,
    chatSessionCompleted,
  });
  const { lineItems, currency, commodityGroupId } = useLineItemsData(data, sessionId);

  const apiRef = useGridApiRef();

  const onSave = async () => {
    const rows = Array.from(apiRef?.current.getRowModels?.().values());
    await save({
      variables: {
        data: {
          chatSessionId: sessionId,
          lineItems: rows.map((row, index) => gridDataToLineItem(row, index)),
        },
      },
    });
  };

  const stepCompleted = status === SvbrgChatMessageStatus.COMPLETED;
  const chatStepConfig = useChatStepState({
    isEditable: true,
    startState: stepCompleted ? ChatStepState.VIEW : ChatStepState.PENDING,
    isCompleted: chatSessionCompleted,
    onAction: async (action) => {
      switch (action) {
        case ChatStepAction.NEXT:
        case ChatStepAction.SAVE:
          await onSave();
          return;
        case ChatStepAction.CANCEL:
          reset();
          return;
      }
    },
  });

  return (
    <ChatStep
      title={tGlobal('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={t('message')}
      footer={<ChatStepActions {...chatStepConfig} loading={loading || updating} />}
    >
      {loading ? (
        <ChatSkeleton />
      ) : (
        <CurrencyProvider value={currency}>
          <IntakeRequestStepLineItems
            disabled={chatStepConfig.readOnly}
            apiRef={apiRef}
            lineItems={lineItems}
            defaultValues={{ commodityGroupId }}
          />
        </CurrencyProvider>
      )}
    </ChatStep>
  );
};
